/**
 * mk-calendarweek
 * ------------------------------------------------------------------------------
 * Steuert eine Selectbox zur Auswahl der Lieferwunschwoche (KW) auf der PDP.
 *
 * Die angebotenen Wochen richten sich nach:
 *   - der Basis-KW des Produkts (Metafeld, Default fuer alle Varianten)
 *   - der Varianten-KW (Metafeld, ueberschreibt die Basis nach oben)
 *   - der aktuellen ISO-Kalenderwoche
 *
 * Ziel-KW = max(baseWeek, variantWeek); liegt sie in der Zukunft, startet die
 * Liste dort und es gibt keine "Schnellstmoeglich"-Option. Sonst beginnt die
 * Liste bei currentWeek + 1 und wird um "Schnellstmoeglich" ergaenzt.
 *
 * Instanzen werden ueber [data-mk-calendarweek] gefunden, die Konfiguration
 * kommt aus dem eingebetteten JSON-Script (siehe snippets/mk-calendarweek.liquid).
 * ------------------------------------------------------------------------------
 */
window.mk_calendarweek = (() => {
    const VERSION = "3.0";
    const DEBUG = false;
    const WEEK_MS = 604800000;

    const selectors = {
        root: "[data-mk-calendarweek]",
        config: "[data-mk-calendarweek-config]",
        select: "[data-mk-calendarweek-select]",
        status: "[data-mk-calendarweek-status]",
        statusText: "[data-mk-calendarweek-status-text]",
        sectionContainer: "[data-js-product-container]",
        productOptions: ".js-product-options",
        selectedVariant: "[data-selected-variant]",
    };

    const classes = {
        inStock: "is-instock",
        statusAvailable: "mk-calendarweek__status--available",
        statusPreorder: "mk-calendarweek__status--preorder",
    };

    const defaults = {
        weeksToShow: 24,
        skipHolidayWeeks: true,
        locale: "de-DE",
        text: {
            asap: "Schnellstmöglich (4-5 Werktage)",
            holidays: "Lieferung ab KW 03 | Nach den Feiertagen",
            holidaysValue: "KW03",
            inStock: "Auf Lager – jetzt versandfertig",
            notInStock: "Hohe Nachfrage – Auslieferung KW",
        },
    };

    const instances = new Map();

    const log = (...args) => {
        if (DEBUG) console.log("📅", ...args);
    };

    /* ---------------------------------------------------------------------- */
    /* ISO-Wochen-Helfer                                                       */
    /* ---------------------------------------------------------------------- */

    // Montag = 0 ... Sonntag = 6
    const isoDayIndex = (date) => (date.getDay() + 6) % 7;

    // Donnerstag der ISO-Woche, in der das Datum liegt. Sein Kalenderjahr ist
    // per Definition das ISO-Wochenjahr.
    const isoThursday = (date) => {
        const thursday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        thursday.setDate(thursday.getDate() + 3 - isoDayIndex(thursday));
        return thursday;
    };

    const getISOWeekNumber = (date = new Date()) => {
        const thursday = isoThursday(date);
        const firstThursday = isoThursday(new Date(thursday.getFullYear(), 0, 4));
        return 1 + Math.round((thursday - firstThursday) / WEEK_MS);
    };

    const getISOWeekYear = (date = new Date()) => isoThursday(date).getFullYear();

    // Der 28.12. liegt immer in der letzten ISO-Woche seines Jahres.
    const getWeeksInYear = (year) => getISOWeekNumber(new Date(year, 11, 28));

    const getMondayOfISOWeek = (week, year) => {
        const firstThursday = isoThursday(new Date(year, 0, 4));
        const monday = new Date(firstThursday);
        monday.setDate(firstThursday.getDate() - 3 + (week - 1) * 7);
        return monday;
    };

    const formatWeekRange = (week, year, locale) => {
        const monday = getMondayOfISOWeek(week, year);
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);

        return {
            start: monday.toLocaleDateString(locale, { day: "numeric", month: "numeric" }),
            end: friday.toLocaleDateString(locale, { day: "numeric", month: "numeric", year: "2-digit" }),
        };
    };

    // Nicht lieferbar: die letzten beiden KW des Jahres sowie KW 1 und KW 2
    // (seit 03.11.2026: KW 2 wird nicht beliefert, erste Lieferwoche im neuen Jahr ist KW 3).
    const isHolidayWeek = (week, year) => {
        const lastWeek = getWeeksInYear(year);
        return week === 1 || week === 2 || week === lastWeek || week === lastWeek - 1;
    };

    const normalizeWeek = (value) => {
        if (value === null || value === undefined || value === "") {
            return 0;
        }

        const parsed = Number(value);

        if (!Number.isInteger(parsed) || parsed < 0) {
            return 0;
        }

        return parsed;
    };

    const padWeek = (week) => String(week).padStart(2, "0");

    /* ---------------------------------------------------------------------- */
    /* Optionen                                                                */
    /* ---------------------------------------------------------------------- */

    const getNextDeliverableWeeks = (instance, startWeek, startYear) => {
        const weeks = [];

        let week = startWeek;
        let year = startYear;
        let guard = 0;

        while (weeks.length < instance.weeksToShow && guard < 500) {
            guard += 1;

            const weeksInYear = getWeeksInYear(year);

            if (week > weeksInYear) {
                week = 1;
                year += 1;
                continue;
            }

            if (!instance.skipHolidayWeeks || !isHolidayWeek(week, year)) {
                const range = formatWeekRange(week, year, instance.locale);

                weeks.push({
                    label: `KW${padWeek(week)} (${range.start} - ${range.end})`,
                    value: `KW${padWeek(week)}`,
                    week,
                    year,
                });
            }

            week += 1;
        }

        return weeks;
    };

    const getDefaultOption = (instance) => {
        if (instance.skipHolidayWeeks && isHolidayWeek(instance.currentWeek, instance.currentYear)) {
            return {
                label: instance.text.holidays,
                value: instance.text.holidaysValue,
            };
        }

        return {
            label: instance.text.asap,
            value: "",
        };
    };

    const buildOptionsForWeek = (instance, week) => {
        if (week === 0) {
            return [
                getDefaultOption(instance),
                ...getNextDeliverableWeeks(instance, instance.currentWeek + 1, instance.currentYear),
            ];
        }

        return getNextDeliverableWeeks(instance, week, instance.currentYear);
    };

    const renderOptions = (instance, options) => {
        const fragment = document.createDocumentFragment();

        options.forEach(({ label, value }) => {
            const option = document.createElement("option");
            option.textContent = label;
            option.value = value;
            fragment.appendChild(option);
        });

        instance.select.innerHTML = "";
        instance.select.appendChild(fragment);
    };

    /* ---------------------------------------------------------------------- */
    /* Zustand                                                                 */
    /* ---------------------------------------------------------------------- */

    // Die Ziel-KW zaehlt nur, wenn sie in der Zukunft liegt.
    const resolveTargetWeek = (instance, variantWeek) => {
        const target = Math.max(normalizeWeek(instance.baseWeek), normalizeWeek(variantWeek));
        return target > instance.currentWeek ? target : 0;
    };

    const updateStatus = (instance) => {
        if (!instance.status || !instance.statusText) return;

        const week = normalizeWeek(String(instance.select.value || "").replace(/^KW/, ""));

        if (week > 0) {
            instance.statusText.textContent = `${instance.text.notInStock} ${padWeek(week)}`;
            instance.status.classList.remove(classes.statusAvailable);
            instance.status.classList.add(classes.statusPreorder);
            return;
        }

        instance.statusText.textContent = instance.text.inStock;
        instance.status.classList.remove(classes.statusPreorder);
        instance.status.classList.add(classes.statusAvailable);
    };

    const updateOptions = (instance, week) => {
        instance.activeWeek = normalizeWeek(week);

        renderOptions(instance, buildOptionsForWeek(instance, instance.activeWeek));
        updateStatus(instance);
    };

    const applyVariant = (instance, variantId, available) => {
        if (!variantId) return;

        const id = String(variantId);

        if (typeof available === "boolean") {
            instance.select.dataset.instock = String(available);

            if (!available) {
                instance.select.classList.remove(classes.inStock);
            }
        }

        if (id === instance.activeVariantId) return;

        instance.activeVariantId = id;

        const targetWeek = resolveTargetWeek(instance, instance.variations[id]);

        if (targetWeek === instance.activeWeek) {
            log("Variante gewechselt, KW unveraendert", { variantId: id, week: targetWeek });
            return;
        }

        log("Variante gewechselt", { variantId: id, week: targetWeek });

        updateOptions(instance, targetWeek);
    };

    /* ---------------------------------------------------------------------- */
    /* Events                                                                  */
    /* ---------------------------------------------------------------------- */

    const whenEventBusReady = (callback) => {
        if (window.themeCore && window.themeCore.EventBus) {
            callback(window.themeCore.EventBus);
            return;
        }

        let done = false;
        let attempts = 0;

        const resolve = () => {
            if (done || !window.themeCore || !window.themeCore.EventBus) return;

            done = true;
            window.clearInterval(timer);
            callback(window.themeCore.EventBus);
        };

        // 10 Sekunden warten, danach greift nur noch der MutationObserver.
        const timer = window.setInterval(() => {
            attempts += 1;
            resolve();

            if (!done && attempts > 100) {
                window.clearInterval(timer);
                log("EventBus nicht verfuegbar, Fallback ueber MutationObserver aktiv");
            }
        }, 100);

        document.addEventListener("theme:all:loaded", resolve, { once: true });
    };

    // Primaerer Kanal: das Theme meldet jeden Variantenwechsel ueber den EventBus.
    const bindEventBus = (instance) => {
        if (!instance.sectionId) return;

        whenEventBusReady((eventBus) => {
            eventBus.listen(`pdp:section-${instance.sectionId}:change-variant`, (data) => {
                if (!data || !data.variantId) return;

                applyVariant(instance, data.variantId, data.variant ? data.variant.available : undefined);
            });
        });
    };

    // Fallback: das Theme rendert bei jedem Variantenwechsel .js-product-options
    // neu; darin steckt die aktuelle Variante als JSON.
    const bindOptionsObserver = (instance) => {
        const container = instance.root.closest(selectors.sectionContainer);
        const optionsContainer = container && container.querySelector(selectors.productOptions);

        if (!optionsContainer) return;

        const observer = new MutationObserver(() => {
            const json = optionsContainer.querySelector(selectors.selectedVariant);

            if (!json) return;

            try {
                const variant = JSON.parse(json.textContent);
                applyVariant(instance, variant && variant.id, variant ? variant.available : undefined);
            } catch (error) {
                log("Variante konnte nicht gelesen werden", error);
            }
        });

        observer.observe(optionsContainer, { childList: true, subtree: true });

        instance.observer = observer;
    };

    const bindSelect = (instance) => {
        instance.select.addEventListener("change", () => {
            if (instance.select.dataset.instock === "true") {
                instance.select.classList.add(classes.inStock);
            }

            updateStatus(instance);
        });
    };

    /* ---------------------------------------------------------------------- */
    /* Init                                                                    */
    /* ---------------------------------------------------------------------- */

    const readConfig = (root) => {
        const script = root.querySelector(selectors.config);

        if (!script) return null;

        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            console.error("📅 mk-calendarweek: Konfiguration ist kein gueltiges JSON", error);
            return null;
        }
    };

    // Die Produkt-Templates liefern noch "KW 02" als Feiertags-Text; erste Lieferwoche ist KW 03.
    const fixHolidayText = (text) => {
        text.holidays = String(text.holidays || "").replace(/KW\s?0?2\b/, "KW 03");
        if (/^KW0?2$/i.test(String(text.holidaysValue || ""))) text.holidaysValue = "KW03";
        return text;
    };

    const createInstance = (root, config) => {
        const select = root.querySelector(selectors.select);

        if (!select) return null;

        const now = new Date();
        const status = root.querySelector(selectors.status);

        return {
            root,
            select,
            status,
            statusText: status ? status.querySelector(selectors.statusText) : null,
            sectionId: config.sectionId,
            baseWeek: normalizeWeek(config.baseWeek),
            variations: config.variations || {},
            weeksToShow: normalizeWeek(config.weeksToShow) || defaults.weeksToShow,
            skipHolidayWeeks: config.skipHolidayWeeks !== false,
            locale: config.locale || defaults.locale,
            text: fixHolidayText({ ...defaults.text, ...(config.text || {}) }),
            currentWeek: getISOWeekNumber(now),
            currentYear: getISOWeekYear(now),
            activeWeek: -1,
            activeVariantId: null,
        };
    };

    const init = (root) => {
        if (!root || root.dataset.mkCalendarweekReady === "true") return null;

        const config = readConfig(root);

        if (!config) return null;

        const instance = createInstance(root, config);

        if (!instance) return null;

        root.dataset.mkCalendarweekReady = "true";
        instances.set(root, instance);

        log("Init", {
            version: VERSION,
            sectionId: instance.sectionId,
            baseWeek: instance.baseWeek,
            variantWeek: config.variantWeek,
            currentWeek: instance.currentWeek,
            currentYear: instance.currentYear,
        });

        bindSelect(instance);
        bindEventBus(instance);
        bindOptionsObserver(instance);

        instance.activeVariantId = config.variantId ? String(config.variantId) : null;

        updateOptions(instance, resolveTargetWeek(instance, config.variantWeek));

        return instance;
    };

    const destroy = (root) => {
        const instance = instances.get(root);

        if (!instance) return;

        if (instance.observer) {
            instance.observer.disconnect();
        }

        instances.delete(root);
        delete root.dataset.mkCalendarweekReady;
    };

    const initAll = (scope = document) => {
        [...scope.querySelectorAll(selectors.root)].forEach(init);
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
    } else {
        initAll();
    }

    // Theme-Editor: Sections werden neu gerendert.
    document.addEventListener("shopify:section:load", (event) => initAll(event.target));
    document.addEventListener("shopify:section:unload", (event) => {
        [...event.target.querySelectorAll(selectors.root)].forEach(destroy);
    });

    return {
        init,
        initAll,
        destroy,
        instances,
        // fuer Tests / Debugging
        utils: { getISOWeekNumber, getISOWeekYear, getWeeksInYear, getMondayOfISOWeek, isHolidayWeek },
    };
})();
